import type { ResponseErrorType } from './error';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** How much of a non-JSON error body is kept for debugging. */
const BODY_PREVIEW_LENGTH = 200;

/**
 * Safely parses an error response, handling cases where the response might not be JSON.
 * If the response is not JSON (e.g., HTML error page), returns a fallback error object
 * carrying the status and a short preview of the body.
 *
 * Shared by the client fetch helpers and the server query helpers so both surface
 * the same `code`s (`PARSE_ERROR`, `NON_JSON_ERROR`) for an unreadable error body.
 */
export async function parseErrorResponse(res: Response): Promise<ResponseErrorType> {
  const contentType = res.headers.get('content-type');

  // Check if response is JSON
  if (contentType?.includes('application/json')) {
    try {
      return (await res.json()) as ResponseErrorType;
    } catch {
      // JSON parsing failed even though content-type says it's JSON
      return {
        code: 'PARSE_ERROR',
        message: 'Failed to parse error response as JSON',
        status: res.status,
      };
    }
  }

  // Response is not JSON (likely HTML error page)
  const textBody = await res.text().catch(() => 'Unable to read response body');
  return {
    code: 'NON_JSON_ERROR',
    message: `Server returned non-JSON error response (${res.status})`,
    status: res.status,
    source: {
      response: {
        contentType: contentType || 'unknown',
        bodyPreview: textBody.substring(0, BODY_PREVIEW_LENGTH),
      },
    },
  };
}
